import {
  AtSymbolIcon, PhoneIcon } from '@heroicons/react/24/solid';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import type { InteractionFormData } from '../../../../@types/contactInteraction';
import type { Owner } from '../../../../@types/Owner';
import type { RootState } from '../../../../store/store';
import MUITable from '../../../common/MUITable';
import CoproLocalOwned from '../../components/CoproLocalOwned';
import OwnerInfos from '../../components/OwnerInfos';
import { LastContactInfo } from '../../components/PreviousContactList';
import InteractionForm from './ToCallForm';

interface ToCallTableProps {
  data: Owner[];
  onInteractionSubmit: (ownerId: number, formData: InteractionFormData) => void;
  // API Pagination props
  apiPagination?: boolean;
  totalCount?: number;
  onPageChange?: (page: number, rowsPerPage: number) => void;
  currentPage?: number;
  currentRowsPerPage?: number;
  onRefresh?: () => void;
}

const ToCallTable: React.FC<ToCallTableProps> = ({ 
  data, 
  onInteractionSubmit,
  apiPagination = false,
  totalCount,
  onPageChange,
  currentPage = 0,
  currentRowsPerPage = 10,
  onRefresh,
}) => {
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const jwt = useSelector((state: RootState) => state.auth.token);

  const handleCallClick = (owner: Owner) => {
    setSelectedOwner(owner);
    setIsFormOpen(true);
  };

  const handleFormSubmit = (formData: InteractionFormData) => {
    if (selectedOwner && selectedOwner.id) {
      onInteractionSubmit(selectedOwner.id, formData);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    if (onRefresh) {
      onRefresh();
    }
  };

  const getContactInfo = (owner: Owner) => {
    return <div className="space-y-1">
      {owner.phone && (
        <div className="flex items-center">
          <PhoneIcon className="mr-2 h-4 w-4 text-gray-500" />
          <a href={`tel:${owner.phone}`} className="text-rgpd-blue hover:underline">
            {owner.phone}
          </a>
        </div>
      )}
      {owner.email && (
        <div className="flex items-center">
          <AtSymbolIcon className="mr-2 h-4 w-4 text-gray-500" />
          <a href={`mailto:${owner.email}`} className="text-rgpd-blue hover:underline">
            {owner.email}
          </a>
        </div>
      )}
    </div>;
  };

  const columns = [
    { title: 'Nom' },
    { title: 'Propriété' },
    { title: 'Coordonnées' },
    { title: 'Dernier contact' },
    { title: 'Actions' },
  ];

  const tableData = data.map(owner => {    
    const actionButton = (
      <div className="flex space-x-2">
        <button
          onClick={() => handleCallClick(owner)}
          className="rounded bg-rgpd-blue px-4 py-2 text-white"
        >
          Appeler
        </button>
      </div>
    );

    return {
      'Nom': <OwnerInfos owner={owner} />,
      'Propriété': <CoproLocalOwned owner={owner} />,
      'Coordonnées': getContactInfo(owner),
      'Dernier contact': <LastContactInfo owner={owner} />,
      'Actions': actionButton,
    };
  });

  return (
    <div>
      <MUITable
        data={tableData}
        headers={columns}
        disablePagination={false}
        apiPagination={apiPagination}
        totalCount={totalCount}
        onPageChange={onPageChange}
        currentPage={currentPage}
        currentRowsPerPage={currentRowsPerPage}
      />

      {isFormOpen && selectedOwner && jwt && (
        <InteractionForm
          owner={selectedOwner}
          isOpen={isFormOpen}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
          jwt={jwt}
        />
      )}
    </div>
  );
};

export default ToCallTable; 